"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";

type DoorDashDeduction = {
  label: { en: string; es: string };
  amount: number;
};

const ITEMS = [
  { name: "Birria Plate", price: 16.50 },
  { name: "Quesabirria Tacos × 2", price: 12.00 },
  { name: "Agua Fresca", price: 4.00 },
];

const SUBTOTAL = ITEMS.reduce((s, i) => s + i.price, 0); // $32.50

// EXAMPLE marketplace deductions — generic, not any specific app's fee schedule.
// Card processing is left out on BOTH sides (see the footnote).
const DOORDASH_DEDUCTIONS: DoorDashDeduction[] = [
  { label: { en: "Commission (15%)", es: "Comisión (15%)" }, amount: 4.88 },
  { label: { en: "Marketing fee", es: "Marketing fee" }, amount: 2.30 },
  { label: { en: "Delivery commission", es: "Comisión delivery" }, amount: 2.27 },
];

const DOORDASH_TOTAL_DEDUCTIONS = DOORDASH_DEDUCTIONS.reduce((s, d) => s + d.amount, 0); // ~$9.45
const DOORDASH_TO_MERCHANT = SUBTOTAL - DOORDASH_TOTAL_DEDUCTIONS; // ~$23.05

export default function OnlineOrderingReceiptCompare() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div
            className="text-[11px] font-black tracking-widest uppercase mb-4"
            style={{ color: "#D7263D" }}
          >
            {t("Same order. Two takes.", "Mismo pedido. Dos tomas.")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal max-w-[800px] mx-auto">
            {t("The middleman, ", "El intermediario, ")}
            <span className="text-redy-charcoal/30">
              {t("itemized.", "desglosado.")}
            </span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary max-w-[560px] mx-auto mt-6">
            {t(
              "An example $32.50 order. The customer pays the same either way. Watch where the money actually goes.",
              "Un pedido de ejemplo de $32.50. El cliente paga lo mismo de cualquier forma. Mira a dónde se va el dinero."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[920px] mx-auto">
          {/* DoorDash receipt */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="bg-[#FAFAF8] rounded-[24px] p-8 border border-black/5"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          >
            <div className="text-center pb-4 border-b border-dashed border-black/15 mb-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-redy-secondary/70">
                {t("Order via a marketplace app", "Orden vía una app de marketplace")}
              </div>
              <div className="text-[18px] font-black mt-1 text-redy-charcoal">
                {t("MARKETPLACE", "MARKETPLACE")}
              </div>
            </div>

            <div className="space-y-3 text-[13px]">
              {ITEMS.map((item, i) => (
                <div key={i} className="flex justify-between text-redy-charcoal">
                  <span>{item.name}</span>
                  <span className="tabular-nums">${item.price.toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-dashed border-black/15 my-4" />

            <div className="flex justify-between text-[12px] text-redy-secondary mb-3">
              <span>{t("Customer pays", "Cliente paga")}</span>
              <span className="tabular-nums font-bold text-redy-charcoal">
                ${SUBTOTAL.toFixed(2)}
              </span>
            </div>

            <div className="border-t border-dashed border-black/15 my-4" />

            {/* Deductions, struck through one by one */}
            <div className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/60 mb-2">
              {t("The app deducts (example)", "La app deduce (ejemplo)")}
            </div>

            <div className="space-y-1.5 text-[12px]">
              {DOORDASH_DEDUCTIONS.map((d, i) => (
                <motion.div
                  key={d.label.en}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.4, delay: 0.5 + 0.15 * i }}
                  className="flex justify-between items-baseline"
                  style={{ color: "#DC2626" }}
                >
                  <span className="relative">
                    {t(d.label.en, d.label.es)}
                    <motion.span
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.5, delay: 0.55 + 0.15 * i, ease: "easeOut" }}
                      className="absolute left-0 top-1/2 w-full h-px origin-left"
                      style={{ backgroundColor: "#DC2626" }}
                    />
                  </span>
                  <span className="tabular-nums">−${d.amount.toFixed(2)}</span>
                </motion.div>
              ))}
            </div>

            <div className="border-t border-dashed border-black/15 my-4" />

            <div className="flex justify-between items-baseline">
              <span className="text-[12px] font-bold uppercase tracking-wider text-redy-secondary/70">
                {t("To merchant", "Al merchant")}
              </span>
              <span className="text-[28px] font-black text-redy-charcoal/50 tabular-nums">
                ${DOORDASH_TO_MERCHANT.toFixed(2)}
              </span>
            </div>

            <div className="mt-4 text-center text-[10px] uppercase tracking-widest font-bold text-redy-secondary/60">
              {t("You keep", "Te quedas con")}{" "}
              <span style={{ color: "#DC2626" }}>
                {Math.round((DOORDASH_TO_MERCHANT / SUBTOTAL) * 100)}%
              </span>
            </div>
          </motion.div>

          {/* Redy receipt */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="bg-white rounded-[24px] p-8 border border-black/5 shadow-[0_20px_60px_rgba(0,0,0,0.04)] relative overflow-hidden"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          >
            <div
              className="absolute -top-10 -right-10 w-[200px] h-[200px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
            />

            <div className="relative">
              <div className="text-center pb-4 border-b border-dashed border-black/15 mb-4">
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-redy-secondary/70">
                  {t("Order via your site", "Orden vía tu sitio")}
                </div>
                <div className="text-[18px] font-black mt-1 text-redy-charcoal">
                  {t("DIRECT", "DIRECTO")}
                </div>
              </div>

              <div className="space-y-3 text-[13px]">
                {ITEMS.map((item, i) => (
                  <div key={i} className="flex justify-between text-redy-charcoal">
                    <span>{item.name}</span>
                    <span className="tabular-nums">${item.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-dashed border-black/15 my-4" />

              <div className="flex justify-between text-[12px] text-redy-secondary mb-3">
                <span>{t("Customer pays", "Cliente paga")}</span>
                <span className="tabular-nums font-bold text-redy-charcoal">
                  ${SUBTOTAL.toFixed(2)}
                </span>
              </div>

              <div className="border-t border-dashed border-black/15 my-4" />

              {/* Single green line */}
              <motion.div
                initial={{ opacity: 0, backgroundColor: "rgba(244,114,43,0)" }}
                whileInView={{ opacity: 1, backgroundColor: "rgba(244,114,43,0.10)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="rounded-[10px] px-3 py-3"
              >
                <div className="flex justify-between text-[12px] font-bold" style={{ color: "#D7263D" }}>
                  <span>{t("Redy fee", "Fee de Redy")}</span>
                  <span className="tabular-nums">$0.00</span>
                </div>
              </motion.div>

              <div className="border-t border-dashed border-black/15 my-4" />

              <div className="flex justify-between items-baseline">
                <span className="text-[12px] font-bold uppercase tracking-wider text-redy-secondary/70">
                  {t("To merchant", "Al merchant")}
                </span>
                <span className="text-[28px] font-black text-redy-charcoal tabular-nums">
                  ${SUBTOTAL.toFixed(2)}
                </span>
              </div>

              <div className="mt-4 text-center text-[10px] uppercase tracking-widest font-bold text-redy-secondary/60">
                {t("Redy commission", "Comisión de Redy")}{" "}
                <span style={{ color: "#D7263D" }}>0%</span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center text-[14px] font-medium text-redy-secondary mt-12 max-w-[600px] mx-auto"
        >
          {t(
            "In this example, $9.45 went to the marketplace app. The customer paid the same. The food was the same. Card processing is left out on both sides — with dual pricing, the card price is designed to cover it.",
            "En este ejemplo, $9.45 se fueron a la app de marketplace. El cliente pagó lo mismo. La comida fue la misma. El procesamiento de tarjeta se deja fuera de los dos lados — con dual pricing, el precio con tarjeta está pensado para cubrirlo."
          )}
        </motion.p>
      </div>
    </section>
  );
}
